import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories, testimonials } from "@/mocks/news";

const BASE_URL = "https://jugxooi.cn";

const milestones = [
  {
    year: "起点",
    title: "从一条热搜时间线开始",
    text: "最初只是把每天的热搜整理成时间线发在朋友圈，后来发现「讲清楚」这件事比「抢首发」更有价值。",
  },
  {
    year: "现在",
    title: "四个固定频道",
    text: "偶像塌房、演员绯闻、歌手翻车、综艺名场面，频道固定下来之后，读者知道该去哪里找自己想看的角度。",
  },
  {
    year: "方向",
    title: "更慢、更完整的复盘",
    text: "未来会增加更多的行业观察与幕后采访，把娱乐新闻从情绪现场变成可以长期阅读的资料。",
  },
];

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "核实优先",
    text: "只讨论已经公开且可以交叉验证的内容，涉及私人行程与家庭信息的部分一律略过。",
  },
  {
    icon: "ri-scales-3-line",
    title: "区分事实与推测",
    text: "存在争议的事件会明确标注信息状态，用「疑似」「据称」把推测与已确认事实分开表达。",
  },
  {
    icon: "ri-emotion-happy-line",
    title: "拒绝人身攻击",
    text: "我们讨论行为与行业现象，不参与对个人的辱骂与标签化，评论区同样执行这一标准。",
  },
  {
    icon: "ri-links-line",
    title: "标注信息来源",
    text: "每一篇复盘都会尽量给出时间、来源与上下文，让读者可以自己判断，而不是被动接受结论。",
  },
];

export default function AboutPage() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于美日吃瓜",
      url: `${BASE_URL}/about`,
      inLanguage: "zh-CN",
      description:
        "美日吃瓜是一个聚焦娱乐圈热点与幕后故事的简体中文资讯频道，坚持核实优先、区分事实与推测、拒绝人身攻击的内容原则。",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "美日吃瓜",
      url: BASE_URL,
      email: "hello@meirichigua.com",
      description:
        "聚焦娱乐圈热点与幕后故事的简体中文资讯频道，覆盖偶像塌房、演员绯闻、歌手翻车与综艺名场面。",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "读者服务",
        email: "hello@meirichigua.com",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE_URL}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${BASE_URL}/about` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="关于我们 - 美日吃瓜 | 娱乐内容的另一种打开方式"
        description="美日吃瓜是聚焦娱乐圈热点与幕后故事的资讯频道。了解我们的品牌定位、发展理念、内容原则与读者口碑，以及如何与我们取得联系。"
        keywords="美日吃瓜关于我们,美日吃瓜品牌介绍,美日吃瓜内容原则,美日吃瓜联系方式"
        canonical="/about"
        jsonLd={jsonLd}
      />
      <Navbar />
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      <main>
        <section className="relative overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20warm%20newsroom%20at%20dusk%20with%20desks%2C%20screens%20and%20hanging%20pendant%20lights%2C%20calm%20professional%20atmosphere%2C%20deep%20crimson%20and%20amber%20tones%2C%20cinematic%20magazine%20visual%20with%20soft%20grain&width=1600&height=800&seq=mrcg-about-01&orientation=landscape"
              alt="美日吃瓜编辑部工作场景意象配图"
              title="关于美日吃瓜"
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/85 to-primary-950/60" />
          </div>
          <div className="relative mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent-400/50 bg-accent-500/10 px-3 py-1 text-xs font-medium tracking-wide text-accent-200">
              <i className="ri-book-open-line" aria-hidden="true" />
              About 美日吃瓜
            </span>
            <h1 className="mt-5 max-w-3xl font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              让每一场娱乐圈热搜，都有据可查
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              美日吃瓜是一个简体中文娱乐资讯频道，专注把碎片化的热点整理成完整、可追溯的内容。我们相信，吃瓜这件事也可以很认真。
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">我们是谁</h2>
              <div className="mt-5 space-y-4 text-[15px] leading-[1.9] text-foreground-700">
                <p>
                  美日吃瓜由一群长期关注娱乐行业的写作者组成。我们不是传统媒体，也不是营销账号，更接近一个持续更新的观察笔记：把每天发生的事记录下来，再尽力解释它为什么值得被讨论。
                </p>
                <p>
                  网站当前设有四个固定频道——偶像塌房、演员绯闻、歌手翻车、综艺名场面。它们并不是为了制造对立，而是因为这几类事件最能反映娱乐圈的运行逻辑：人设如何建立、舆论如何发酵、行业规则又如何被一次次试探。
                </p>
                <p>
                  站内出现的人物姓名多为化名，事件用于说明典型现象，请勿与真实个体一一对应。我们更希望提供的是一种理性的吃瓜视角，而不是另一份八卦清单。
                </p>
              </div>

              <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950">网站定位</h2>
              <p className="mt-5 text-[15px] leading-[1.9] text-foreground-700">
                我们把自己定位为「热搜之后」的一站式阅读入口。当一条热搜出现时，你可以在美日吃瓜找到它的完整时间线、各方回应与关键争议点，用五分钟建立起比刷一整天碎片信息更清晰的认知。
              </p>
            </div>

            <aside className="lg:col-span-6 lg:pl-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                <h2 className="font-heading text-lg font-bold text-foreground-950">发展理念</h2>
                <ol className="mt-6 space-y-6 border-l border-background-300 pl-6">
                  {milestones.map((item) => (
                    <li key={item.year} className="relative">
                      <span className="absolute -left-[30px] top-1.5 flex h-3 w-3 items-center justify-center rounded-full border-2 border-background-100 bg-accent-500" />
                      <span className="text-xs font-bold tracking-[0.2em] text-primary-700">
                        {item.year}
                      </span>
                      <h3 className="mt-1 font-heading text-base font-bold text-foreground-900">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                        {item.text}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">内容原则</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              这四条是我们所有内容都必须遵守的底线，它们决定了我们会写什么，也决定了我们不写什么。
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
                    {item.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">用户口碑</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            来自读者的一些反馈。我们保留原文，也保留那些不那么好听的意见作为改进方向。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-3">
                  <span className="block text-sm font-bold text-foreground-900">{item.name}</span>
                  <span className="mt-0.5 block text-xs text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-content px-4 pb-16 md:px-6 md:pb-20">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <div className="p-6 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-foreground-950">联系我们</h2>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                    内容建议、线索投稿、纠错反馈或合作咨询，都欢迎通过邮件联系我们。我们会在三个工作日内回复，稿件请尽量注明信息来源。
                  </p>
                  <div className="mt-6 space-y-3">
                    <a
                      href="mailto:hello@meirichigua.com"
                      rel="nofollow"
                      className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3 transition-colors hover:border-primary-300"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                        <i className="ri-mail-line text-lg" aria-hidden="true" />
                      </span>
                      <span>
                        <span className="block text-xs text-foreground-500">读者服务与投稿</span>
                        <span className="block text-sm font-medium text-foreground-900">
                          hello@meirichigua.com
                        </span>
                      </span>
                    </a>
                    <div className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className="ri-time-line text-lg" aria-hidden="true" />
                      </span>
                      <span>
                        <span className="block text-xs text-foreground-500">回复时间</span>
                        <span className="block text-sm font-medium text-foreground-900">
                          工作日 10:00 - 19:00
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-background-200 p-6 md:p-10 lg:col-span-5 lg:border-l lg:border-t-0">
                <h3 className="font-heading text-base font-bold text-foreground-950">浏览频道</h3>
                <ul className="mt-4 space-y-2.5">
                  {categories.map((item) => (
                    <li key={item.slug}>
                      <Link
                        to={`/category/${item.slug}`}
                        className="group flex items-center justify-between rounded-md border border-background-200 px-3.5 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                      >
                        <span>
                          <span className="block font-medium">{item.name}</span>
                          <span className="mt-0.5 block text-xs text-foreground-500">
                            {item.short}
                          </span>
                        </span>
                        <i
                          className="ri-arrow-right-s-line text-foreground-400 transition-transform group-hover:translate-x-0.5"
                          aria-hidden="true"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}